import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { List, Section } from './LegalPage';
import { LEGAL } from '../../config/legal';

const Terms: React.FC = () => (
  <LegalPage
    title="Terms of Use"
    summary={`The agreement between you and ${LEGAL.SITE_NAME} when you use the service.`}
  >
    <Section heading="The short version">
      <p style={{ margin: 0 }}>
        Use it to learn, don't abuse it, don't try to break it, and understand that an AI grades your
        work and will sometimes be wrong. It's free today and provided as-is.
      </p>
    </Section>

    <Section heading="Agreeing to these terms">
      <p style={{ margin: 0 }}>
        By creating an account or using {LEGAL.SITE_NAME}, you agree to these terms and to the{' '}
        <Link to="/privacy" style={{ color: 'var(--ember-strong)', fontWeight: 700, textDecoration: 'none' }}>Privacy Policy</Link>.
        If you do not agree, do not use the service. You must be at least 16 years old.
      </p>
    </Section>

    <Section heading="Your account">
      <List
        items={[
          'Give accurate details when you sign up, and keep your email address current so you can recover your account.',
          'You are responsible for what happens under your account. Keep your password to yourself.',
          'One account per person. Do not share logins.',
          <>Tell us at <strong>{LEGAL.CONTACT_EMAIL}</strong> if you think someone else has access to your account.</>,
        ]}
      />
    </Section>

    <Section heading="What the service is — and what it isn't">
      <p style={{ margin: 0 }}>
        {LEGAL.SITE_NAME} teaches prompt engineering and agentic AI through exercises graded by a
        large language model. That grading is automated and imperfect: it can mark good work down,
        miss mistakes, and give explanations that are confidently wrong. Scores, badges and levels are
        learning feedback, not a professional assessment, a certification, or evidence of competence
        for any employer or regulator. Nothing here is legal, financial, medical or professional
        advice, and course content may be out of date relative to the frameworks it describes.
      </p>
    </Section>

    <Section heading="Acceptable use">
      <p style={{ margin: '0 0 4px' }}>Do not:</p>
      <List
        items={[
          'Submit unlawful content, or anything designed to harass, defame or harm someone.',
          'Use the service to develop malware, run attacks, or produce material that sexually exploits children — this will be reported.',
          'Put confidential information, personal data about other people, or secrets and credentials into prompts.',
          'Attempt to bypass the safety guardrails, extract system prompts, or manipulate the grader into awarding scores you did not earn.',
          'Scrape, automate, resell or rate-limit-abuse the service, or use it to build a competing dataset.',
          'Probe, scan or attempt to breach the infrastructure, or access another user’s account or data.',
        ]}
      />
      <p style={{ margin: '10px 0 0' }}>
        We may suspend or remove an account that breaks these rules. Where it is practical and lawful
        to do so, we will tell you why.
      </p>
    </Section>

    <Section heading="Who owns what">
      <p style={{ margin: 0 }}>
        The prompts you write stay yours. You grant us the limited permission needed to run the
        service with them — storing them, sending them to the AI model for grading, and showing them
        back to you. We claim no other rights over your work.
      </p>
      <p style={{ margin: '12px 0 0' }}>
        The lessons, challenges, curriculum, design, code, name and mascot of {LEGAL.SITE_NAME}{' '}
        belong to {LEGAL.ENTITY_NAME}. You may use them to learn. You may not republish or resell
        them.
      </p>
      <p style={{ margin: '12px 0 0' }}>
        Output produced by the AI model is subject to the model provider's terms. Like all AI output,
        it may not be unique to you, and we make no claim about who owns it.
      </p>
    </Section>

    <Section heading="Availability, and changes to the service">
      <p style={{ margin: 0 }}>
        This is a service under active development. Features will change, move and sometimes
        disappear. We do not promise any level of uptime, we may take it down for maintenance without
        notice, and we may discontinue it entirely — if we do, we will give you reasonable notice and
        a way to export your data.
      </p>
    </Section>

    <Section heading="Price">
      <p style={{ margin: 0 }}>
        {LEGAL.SITE_NAME} is free today, and no payment details are collected. If paid plans are
        introduced, existing free features will not be silently moved behind a paywall without
        notice, and you will never be charged without explicitly agreeing first.
      </p>
    </Section>

    <Section heading="Ending your account">
      <p style={{ margin: 0 }}>
        You can stop using the service at any time and ask us to delete your account — see{' '}
        <Link to="/data-rights" style={{ color: 'var(--ember-strong)', fontWeight: 700, textDecoration: 'none' }}>your data rights</Link>.
        We may suspend or end an account that breaks these terms, or that we are required to act on
        by law.
      </p>
    </Section>

    <Section heading="Disclaimer and liability">
      <p style={{ margin: 0 }}>
        The service is provided "as is" and "as available", without warranties of any kind, to the
        fullest extent the law allows. To the extent permitted by law, {LEGAL.ENTITY_NAME} is not
        liable for indirect or consequential loss, lost data, or lost profits arising from your use
        of the service.
      </p>
      <p style={{ margin: '12px 0 0' }}>
        Nothing in these terms limits liability that cannot lawfully be limited — including death or
        personal injury caused by negligence, or fraud. If you are a consumer, you keep every right
        your local consumer law gives you, and nothing here overrides it.
      </p>
    </Section>

    <Section heading="Governing law">
      <p style={{ margin: 0 }}>
        These terms are governed by the law of {LEGAL.GOVERNING_LAW}, and its courts have
        jurisdiction over any dispute — subject to any right you have as a consumer to bring a claim
        where you live.
      </p>
    </Section>

    <Section heading="Changes to these terms">
      <p style={{ margin: 0 }}>
        We will update this page and the "last updated" date when these terms change, and email you
        if the change materially affects your rights. Continuing to use the service after a change
        means you accept it.
      </p>
    </Section>
  </LegalPage>
);

export default Terms;
